import React, { useEffect, useMemo, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { MenuBtn } from "components/shared/buttons";
import { LocationSearch } from "components/shared/searchBar";
import { IoIosArrowBack, IoIosArrowDown } from "react-icons/io";
import {
    MdOutlineCloudUpload,
    MdOutlineCategory,
    MdOutlineDrafts,
    MdOutlineEventNote,
    MdOutlineListAlt,
    MdOutlineMailOutline,
    MdOutlinePalette,
    MdOutlinePreview,
    MdOutlineVideoLibrary,
    MdOutlineWidgets,
} from "react-icons/md";
import { RiDashboardLine, RiLogoutBoxRLine, RiTeamLine } from "react-icons/ri";
import { TbSeo } from "react-icons/tb";
import { config } from "config/config";
import { cmsSegments } from "constants/cmsSegments";
import { canAccessSegment } from "utils/accessControl";
import { logout } from "Slices/authSlice";
import { showToast } from "components/shared/Toast";
import { buildEditDetailsPath, buildPreviewPath } from "utils/locationSlug";

const formatRoleLabel = (user) => {
    if (user?.isSuperAdmin || user?.role === "superAdmin") return "Super Admin";
    if (user?.role === "districtManager") return "District Manager";
    if (user?.role === "subAdmin") return "Sub Admin";
    if (user?.role === "editor") return "Editor";
    return "Team Member";
};

const pathMatches = (pathname, matchers = []) =>
    matchers.some((matcher) => matcher.test(pathname));

const slugifyKey = (value = "") =>
    value
        .toLowerCase()
        .replace(/[^a-z0-9]+/g, "-")
        .replace(/^-+|-+$/g, "");

const resolveContentPath = (segment) => {
    if (!segment?.link) {
        return "/cms";
    }

    if (segment.title === "Trek") {
        return "/cms/trek/all-locations";
    }

    if (segment.title === "Travel") {
        return "/cms/travel/all-locations";
    }

    return segment.link;
};

const buildPrimaryNavItems = ({ user, isSuperAdmin, isDistrictManager }) => {
    const items = [];
    const canAccessContentSegment = (segmentName) =>
        isSuperAdmin || canAccessSegment(user, segmentName);

    const contentChildren = [
        {
            key: "content-all",
            label: "All",
            path: "/cms",
            matchers: [/^\/cms$/i],
        },
        ...cmsSegments
            .filter((segment) => segment.disabled || canAccessContentSegment(segment.title))
            .map((segment) => ({
                key: `content-${slugifyKey(segment.title)}`,
                label: segment.title,
                path: resolveContentPath(segment),
                matchers: [new RegExp(`^${segment.link.replace(/[.*+?^${}()|[\]\\]/g, "\\$&")}`, "i")],
                disabled: !!segment.disabled,
            })),
    ];

    if (isSuperAdmin) {
        items.push({
            key: "dashboard",
            label: "Dashboard",
            hint: "Overview",
            icon: <RiDashboardLine />,
            path: "/dashboard",
            matchers: [/^\/dashboard/i],
        });
    }

    items.push({
        key: "content",
        label: "Content",
        hint: "CMS",
        icon: <MdOutlineWidgets />,
        path: "/cms",
        matchers: [/^\/cms(\/(?!appearance|seo).*)?$/i],
        children: contentChildren,
    });

    items.push({
        key: "categories",
        label: "Categories",
        hint: "Taxonomy",
        icon: <MdOutlineCategory />,
        path: "/cms/categories",
        matchers: [/^\/cms\/categories/i],
    });

    if (isSuperAdmin) {
        items.push({
            key: "appearance",
            label: "Appearance",
            hint: "Menus",
            icon: <MdOutlinePalette />,
            path: "/cms/appearance",
            matchers: [/^\/cms\/appearance/i],
            children: [
                {
                    key: "appearance-footer-menu",
                    label: "Footer Menu",
                    path: "/cms/appearance",
                    matchers: [/^\/cms\/appearance/i],
                },
            ],
        });
    }

    items.push({
        key: "seo",
        label: "SEO",
        hint: "Search",
        icon: <TbSeo />,
        path: "/cms/seo",
        matchers: [/^\/cms\/seo/i],
    });

    items.push({
        key: "enquiries",
        label: "Enquiries",
        hint: "Inbox",
        icon: <MdOutlineMailOutline />,
        path: "/enquiry",
        matchers: [/^\/enquiry/i],
    });

    if (isSuperAdmin || isDistrictManager) {
        items.push({
            key: "role-manager",
            label: "Role Manager",
            hint: isSuperAdmin ? "Admin Team" : "My Team",
            icon: <RiTeamLine />,
            path: isSuperAdmin ? "/roleManager" : `/roleManager/profile/${user?.id || ""}`,
            matchers: [/^\/roleManager/i],
        });
    }

    return items;
};

const getAutoExpandedGroups = (items, pathname) =>
    items.reduce((accumulator, item) => {
        if (!item.children?.length) {
            return accumulator;
        }

        const childMatchers = item.children.flatMap((child) => child.matchers || []);
        accumulator[item.key] = pathMatches(pathname, [...item.matchers, ...childMatchers]);
        return accumulator;
    }, {});

const travelEditorForms = [
    { key: "Overview", label: "Overview" },
    { key: "ThumbnailImages", label: "Thumbnail Images" },
    { key: "MajorAttractions", label: "Major Attractions" },
    { key: "BestPlaces", label: "Best Places" },
    { key: "MustExperince", label: "Must Experince" },
    { key: "Festival", label: "Festival" },
    { key: "MustBuy", label: "Must Buy" },
    { key: "FamousFood", label: "Famous Food" },
    { key: "StapleFood", label: "Staple Food" },
    { key: "HowtoReach", label: "How to Reach" },
    { key: "imageGallery", label: "Image Gallery" },
];

const trekEditorForms = [
    { key: "Overview", label: "Overview" },
    { key: "GradeWiseInsights", label: "Grade Wise Insights" },
    { key: "ThumbnailImages", label: "Thumbnail Images" },
    { key: "MajorAttractions", label: "Major Attractions" },
    { key: "HowtoReach", label: "How to Reach" },
    { key: "SuggestedItinerary", label: "Suggested Itinerary" },
    { key: "ImageGallery", label: "Image Gallery" },
    { key: "LocalGuide", label: "Local Guide" },
    { key: "TrekProducts", label: "Trek Products" },
];

const SidebarShell = ({
    navEyebrow = "Admin Menu",
    navContent,
    contextEyebrow,
    contextTitle,
    contextDescription,
    children,
}) => {
    const dispatch = useDispatch();
    const navigate = useNavigate();
    const location = useLocation();
    const { user } = useSelector((state) => state.auth);
    const isSuperAdmin = user?.role === "superAdmin" || user?.isSuperAdmin;
    const isDistrictManager = user?.role === "districtManager";
    const contextItems = React.Children.toArray(children).filter(Boolean);
    const [isLoggingOut, setIsLoggingOut] = useState(false);

    const primaryNavItems = useMemo(
        () => buildPrimaryNavItems({ user, isSuperAdmin, isDistrictManager }),
        [user, isSuperAdmin, isDistrictManager]
    );
    const [expandedGroups, setExpandedGroups] = useState(() =>
        getAutoExpandedGroups(primaryNavItems, location.pathname)
    );

    useEffect(() => {
        const autoExpandedGroups = getAutoExpandedGroups(primaryNavItems, location.pathname);

        setExpandedGroups((previous) => {
            const nextState = { ...previous };

            Object.entries(autoExpandedGroups).forEach(([key, shouldOpen]) => {
                if (shouldOpen) {
                    nextState[key] = true;
                } else if (!(key in nextState)) {
                    nextState[key] = false;
                }
            });

            return nextState;
        });
    }, [location.pathname, primaryNavItems]);

    const toggleGroup = (groupKey) => {
        setExpandedGroups((previous) => ({
            ...previous,
            [groupKey]: !previous[groupKey],
        }));
    };

    const handleLogout = async () => {
        if (isLoggingOut) {
            return;
        }

        try {
            setIsLoggingOut(true);
            await dispatch(logout()).unwrap();
            localStorage.removeItem("token");
            showToast("Logged out successfully.", "success");
            navigate("/", { replace: true });
        } catch (error) {
            showToast(typeof error === "string" ? error : "Logout failed.", "error");
        } finally {
            setIsLoggingOut(false);
        }
    };

    return (
        <aside id="menuBar" className="adminSidebar">
            <div className="adminSidebar__panel">
                <div className="adminSidebar__scroll">
                    <div className="adminSidebar__section">
                        <p className="adminSidebar__eyebrow">{navEyebrow}</p>
                        {navContent || (
                            <div className="adminSidebar__navList">
                                {primaryNavItems.map((item) => {
                                    const isActive = pathMatches(location.pathname, item.matchers);
                                    const isExpanded = Boolean(expandedGroups[item.key]);

                                    if (item.children?.length) {
                                        return (
                                            <div
                                                key={item.key}
                                                className={`adminSidebar__navGroup ${isActive ? "is-active" : ""} ${isExpanded ? "is-expanded" : ""}`}
                                            >
                                                <button
                                                    type="button"
                                                    className={`adminSidebar__navItem adminSidebar__navItem--group ${isActive ? "is-active" : ""}`}
                                                    onClick={() => toggleGroup(item.key)}
                                                    aria-expanded={isExpanded}
                                                    aria-label={`Toggle ${item.label} menu`}
                                                >
                                                    <span className="adminSidebar__navIcon">{item.icon}</span>
                                                    <span className="adminSidebar__navCopy">
                                                        <strong>{item.label}</strong>
                                                    </span>
                                                    <span className={`adminSidebar__navChevron ${isExpanded ? "is-open" : ""}`}>
                                                        <IoIosArrowDown />
                                                    </span>
                                                </button>

                                                {isExpanded && (
                                                    <div className="adminSidebar__subnav">
                                                        {item.children.map((child) => {
                                                            const isChildActive = pathMatches(location.pathname, child.matchers);

                                                            return (
                                                                <button
                                                                    key={child.key}
                                                                    type="button"
                                                                    className={`adminSidebar__subnavItem ${isChildActive ? "is-active" : ""} ${child.disabled ? "is-disabled" : ""}`}
                                                                    onClick={() => {
                                                                        if (!child.disabled) {
                                                                            navigate(child.path);
                                                                        }
                                                                    }}
                                                                    disabled={child.disabled}
                                                                >
                                                                    {child.label}
                                                                </button>
                                                            );
                                                        })}
                                                    </div>
                                                )}
                                            </div>
                                        );
                                    }

                                    return (
                                        <button
                                            key={item.key}
                                            type="button"
                                            className={`adminSidebar__navItem ${isActive ? "is-active" : ""}`}
                                            onClick={() => navigate(item.path)}
                                        >
                                            <span className="adminSidebar__navIcon">{item.icon}</span>
                                            <span className="adminSidebar__navCopy">
                                                <strong>{item.label}</strong>
                                            </span>
                                        </button>
                                    );
                                })}
                            </div>
                        )}
                    </div>

                    {contextItems.length > 0 && (
                        <div className="adminSidebar__section adminSidebar__section--context">
                            {(contextEyebrow || contextTitle || contextDescription) && (
                                <div className="adminSidebar__contextHeader">
                                    {contextEyebrow ? <p className="adminSidebar__eyebrow">{contextEyebrow}</p> : null}
                                    {contextTitle ? <h4>{contextTitle}</h4> : null}
                                    {contextDescription ? <p className="adminSidebar__description">{contextDescription}</p> : null}
                                </div>
                            )}
                            <div className="btnList">{contextItems}</div>
                        </div>
                    )}
                </div>

                <div className="adminSidebar__logoutCard">
                    <div className="adminSidebar__logoutMeta">
                        <span className="adminSidebar__footerLabel">Logged in as</span>
                        <strong>{user?.name || user?.id || "Admin"}</strong>
                        <span className="adminSidebar__footerMeta">
                            {formatRoleLabel(user)}
                            {user?.id ? ` • ${user.id}` : ""}
                        </span>
                    </div>
                    <button
                        type="button"
                        className="adminSidebar__logoutButton"
                        onClick={handleLogout}
                        disabled={isLoggingOut}
                    >
                        <RiLogoutBoxRLine />
                        <span>{isLoggingOut ? "Logging out..." : "Logout"}</span>
                    </button>
                </div>
            </div>
        </aside>
    );
};

const MenuBar = () => <SidebarShell />;

const TravelMenubar = ({ setActiveComponent, setSearchTerm }) => {
    const navigateToAllLocationsTable = () => {
        setActiveComponent("AllLocationsTable");
    };

    return (
        <SidebarShell
            contextEyebrow="Travel"
            contextTitle="Section Tools"
            contextDescription="Filter travel locations and jump back to the main listing."
        >
            <LocationSearch setSearchTerm={setSearchTerm} placeholder={"Search Location"} />
            <MenuBtn
                icon={<MdOutlineListAlt />}
                title={"All Locations"}
                onClick={navigateToAllLocationsTable}
                className="menubtn--sidebar"
            />
        </SidebarShell>
    );
};

const TravelEditDetailsMenubar = ({ selectedForm, setActiveComponent, currentLocation }) => {
    const navigate = useNavigate();

    const handleAllLocationsClick = () => {
        navigate("/cms/travel/all-locations", { replace: true });
    };

    const handlePreviewClick = () => {
        const previewPath = buildPreviewPath("travel", currentLocation);
        if (previewPath) {
            window.open(`${config.clientPath}${previewPath}`, "_blank", "noopener,noreferrer");
        }
    };

    const handleFormSelection = (formName) => {
        setActiveComponent(formName);
        if (currentLocation) {
            navigate(buildEditDetailsPath("travel", currentLocation, formName), { replace: true });
        }
    };

    return (
        <SidebarShell
            navEyebrow="Travel Forms"
            navContent={
                <div className="adminSidebar__formList adminSidebar__formList--primary">
                    {travelEditorForms.map((form) => {
                        const isActive = form.key === selectedForm;

                        return (
                            <button
                                key={form.key}
                                type="button"
                                onClick={() => handleFormSelection(form.key)}
                                className={`adminSidebar__formButton ${isActive ? "is-active" : ""}`}
                            >
                                {form.label}
                            </button>
                        );
                    })}
                </div>
            }
        >
            <MenuBtn
                icon={<MdOutlinePreview />}
                title={"Page Preview"}
                onClick={handlePreviewClick}
                className="menubtn--sidebar"
            />
            <MenuBtn
                icon={<IoIosArrowBack />}
                title={"All Locations"}
                onClick={handleAllLocationsClick}
                className="menubtn--sidebar"
            />
        </SidebarShell>
    );
};

const TrekEditDetailsMenubar = ({ selectedForm, setActiveComponent, currentLocation }) => {
    const navigate = useNavigate();

    const handleAllLocationsClick = () => {
        navigate("/cms/trek/all-locations", { replace: true });
    };

    const handlePreviewClick = () => {
        const previewPath = buildPreviewPath("trek", currentLocation);
        if (previewPath) {
            window.open(`${config.clientPath}${previewPath}`, "_blank", "noopener,noreferrer");
        }
    };

    return (
        <SidebarShell
            navEyebrow="Trek Forms"
            navContent={
                <div className="adminSidebar__formList adminSidebar__formList--primary">
                    {trekEditorForms.map((form) => {
                        const isActive = form.key === selectedForm;

                        return (
                            <button
                                key={form.key}
                                type="button"
                                onClick={() => {
                                    setActiveComponent(form.key);
                                    if (currentLocation) {
                                        navigate(buildEditDetailsPath("trek", currentLocation, form.key), { replace: true });
                                    }
                                }}
                                className={`adminSidebar__formButton ${isActive ? "is-active" : ""}`}
                            >
                                {form.label}
                            </button>
                        );
                    })}
                </div>
            }
        >
            <MenuBtn
                icon={<MdOutlinePreview />}
                title={"Page Preview"}
                onClick={handlePreviewClick}
                className="menubtn--sidebar"
            />
            <MenuBtn
                icon={<IoIosArrowBack />}
                title={"All Locations"}
                onClick={handleAllLocationsClick}
                className="menubtn--sidebar"
            />
        </SidebarShell>
    );
};

const ImageGalleryMenuBar = ({ setActiveComponent }) => {
    return (
        <SidebarShell
            contextEyebrow="Media"
            contextTitle="Gallery Tools"
            contextDescription="Upload and organize image assets for the website."
        >
            <MenuBtn
                icon={<MdOutlineCloudUpload />}
                title={"Upload Images"}
                onClick={() => setActiveComponent("uploadImages")}
                className="menubtn--sidebar"
            />
        </SidebarShell>
    );
};

const YoutubeVideosMenubar = ({ setActiveComponent, setSearchTerm }) => {
    return (
        <SidebarShell
            contextEyebrow="Videos"
            contextTitle="Video Library"
            contextDescription="Search the library or jump back to the full video list."
        >
            <LocationSearch setSearchTerm={setSearchTerm} placeholder={"Search Location"} />
            <MenuBtn
                icon={<MdOutlineVideoLibrary />}
                title={"All Videos"}
                onClick={() => setActiveComponent("videoTable")}
                className="menubtn--sidebar"
            />
        </SidebarShell>
    );
};

const EventsMenubar = ({ setActiveComponent }) => {
    return (
        <SidebarShell
            contextEyebrow="Events"
            contextTitle="Event Manager"
            contextDescription="Review all events or jump straight into drafts."
        >
            <MenuBtn
                icon={<MdOutlineEventNote />}
                title={"All Events"}
                onClick={() => setActiveComponent("eventTabel")}
                className="menubtn--sidebar"
            />
            <MenuBtn
                icon={<MdOutlineDrafts />}
                title={"Drafts"}
                onClick={() => setActiveComponent("Drafts")}
                className="menubtn--sidebar"
            />
        </SidebarShell>
    );
};

const TrekMenubar = () => <SidebarShell />;

const TrashMenubar = ({ setSearchTerm }) => {
    const navigate = useNavigate();

    return (
        <SidebarShell
            contextEyebrow="Maintenance"
            contextTitle="Trash"
            contextDescription="Search deleted content or jump back to the CMS overview."
        >
            <LocationSearch setSearchTerm={setSearchTerm} placeholder={"Search Location"} />
            <MenuBtn
                icon={<MdOutlineWidgets />}
                title={"CMS Overview"}
                onClick={() => navigate("/cms")}
                className="menubtn--sidebar"
            />
        </SidebarShell>
    );
};

export {
    MenuBar,
    TravelMenubar,
    TravelEditDetailsMenubar,
    ImageGalleryMenuBar,
    YoutubeVideosMenubar,
    EventsMenubar,
    TrekMenubar,
    TrekEditDetailsMenubar,
    TrashMenubar,
};
